'use client'

import { Image } from '@macarongfactory/web-ui'
import { useRouter } from 'next/navigation'
import { overlay } from 'overlay-kit'
import { useState } from 'react'

import {
  getPlaceSeoHashId,
  type IMacarongPlace,
  useCurrentLocationStore,
} from '@/entities/place'
import { IconProfileFill } from '@/shared/assets/icons'
import {
  ListHeader,
  ListHeaderLowerText,
  ListHeaderRightImage,
  ListHeaderTitleText,
  ListHeaderTopLabel,
} from '@/shared/design-system/components'
import { logEvent } from '@/shared/external/firebase/analytics'
import { SEO_ONELINK_CONFIG, useOneLink } from '@/shared/external/onelink'
import { DEFAULT_CURRENT_LOCATION } from '@/shared/lib'
import { EVENT_NAMES, LABELS } from '@/shared/lib/analytics'
import { ConfirmDialog } from '@/shared/ui/dialogs'

const PlaceSearchItem = ({
  place,
  isRecommended = false,
}: {
  place: IMacarongPlace
  isRecommended?: boolean
}) => {
  const { get } = useCurrentLocationStore()
  const { handleOneLinkClick, generateLink } = useOneLink(SEO_ONELINK_CONFIG)
  const [imageError, setImageError] = useState(false)
  const router = useRouter()

  const handleClickPlaceItem = async () => {
    try {
      logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
        label: LABELS.WEB_VIEW.SEO.CLICK.PARTNERS_LIST,
        store_name: place.placeName,
        distance: place.distance,
        is_trusted: place.trustedPartner,
      })

      if (!place.trustedPartner) {
        overlay.open(({ isOpen, close }) => (
          <ConfirmDialog
            open={isOpen}
            onClose={close}
            onCancel={close}
            title={`앱으로 더 많은 정비소\n 정보를 확인하세요`}
            description={
              place.statistic.reviewCount && place.statistic.reviewCount >= 50
                ? `${place.statistic.reviewCount}개의 정비 후기를 볼 수 있어요!`
                : '500만 유저들이 남긴 정비 후기를 볼 수 있어요!'
            }
            imageUrl='https://mycle.co.kr/resources/images/graphic/img_guide_mycle_review.png'
            cancelButtonText='닫기'
            confirmButtonText='마이클 앱 설치하기'
            onConfirm={async () => {
              await handleOneLinkClick()
              logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
                label: LABELS.WEB_VIEW.SEO.CLICK.APP_MODAL_INSTALL,
              })
              close()
            }}
          />
        ))
        return
      }

      const detailUrl = await getPlaceSeoHashId({ serverId: place.serverId })
      // af_sub3에 정비소 ID를 주입해 검색→상세 전환을 정비소 단위로 집계 (WEB-4236)
      const onelinkUrl = await generateLink({ afSub3: String(place.serverId) })
      const finalPath = onelinkUrl
        ? `/place/${detailUrl}?seoOnelink=${encodeURIComponent(onelinkUrl)}`
        : `/place/${detailUrl}`
      router.push(finalPath)
    } catch {
      // 상세 페이지 이동 실패는 무시
    }
  }

  // 거리 표시 여부를 판단하는 헬퍼 함수
  const shouldShowDistance = () => {
    return (
      get('latitude') !== DEFAULT_CURRENT_LOCATION.LATITUDE &&
      get('longitude') !== DEFAULT_CURRENT_LOCATION.LONGITUDE &&
      place.distance != null &&
      !isRecommended
    )
  }

  // 하단 텍스트를 구성하는 헬퍼 함수
  const getLowerText = () => {
    const parts: string[] = []

    // 거리 정보 추가
    if (shouldShowDistance()) {
      parts.push(`${place.distance!.toFixed(2)}km`)
    }

    // 리뷰 수 추가
    const reviewCount = Math.max(
      0,
      place.statistic?.reviewCount ?? 0
    ).toLocaleString()
    parts.push(`리뷰 ${reviewCount}`)

    // 정비 이력 추가
    const diaryCount = place.statistic?.diaryCount?.toLocaleString() ?? '0'
    parts.push(`정비 이력 ${diaryCount}`)

    return parts.join(' · ')
  }

  return (
    <ListHeader
      handleClick={handleClickPlaceItem}
      top={place.trustedPartner && <ListHeaderTopLabel text='마이클 예약' />}
      title={
        <ListHeaderTitleText
          title={place.placeName}
          typography='text-title-1-semibold'
          className='line-clamp-1'
        />
      }
      lower={
        <ListHeaderLowerText
          text={getLowerText()}
          typography='text-body-2-medium'
        />
      }
      right={
        <ListHeaderRightImage
          content={
            place.logoImage?.thumbnailUrl && !imageError ? (
              <Image
                src={`${place.logoImage.thumbnailUrl}?w=96&q=75`}
                srcSet={`${place.logoImage.thumbnailUrl}?w=48&q=75 1x, ${place.logoImage.thumbnailUrl}?w=96&q=75 2x`}
                alt='image'
                width={48}
                height={48}
                radius={0}
                onError={() => {
                  setImageError(true)
                }}
              />
            ) : (
              <IconProfileFill fill='#CFD5D9' width='24' height='24' />
            )
          }
        />
      }
    />
  )
}

export default PlaceSearchItem
